import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

@customElement('hg-button')
export class HgButton extends LitElement {
  static styles = css`
    :host {
      display: inline-flex;
      vertical-align: middle;
    }
    a,
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.45rem;
      min-height: 2.5rem;
      padding: 10px 18px;
      border: 1px solid var(--hg-ink);
      border-radius: 0;
      background: transparent;
      color: var(--hg-ink);
      font-family: inherit;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: 0.3px;
      cursor: pointer;
      text-decoration: none;
      transition: background var(--md-sys-motion), color var(--md-sys-motion);
    }
    a:hover,
    button:hover:not(:disabled) {
      background: var(--hg-ink);
      color: var(--hg-paper);
    }
    :host([variant='filled']) a,
    :host([variant='filled']) button,
    :host([variant='tonal']) a,
    :host([variant='tonal']) button {
      background: var(--hg-forest);
      border-color: var(--hg-forest);
      color: #fff;
    }
    :host([variant='filled']) a:hover,
    :host([variant='filled']) button:hover:not(:disabled),
    :host([variant='tonal']) a:hover,
    :host([variant='tonal']) button:hover:not(:disabled) {
      background: var(--hg-forest-deep);
      border-color: var(--hg-forest-deep);
      color: #fff;
    }
    :host([variant='outlined']) a,
    :host([variant='outlined']) button {
      background: transparent;
      border-color: var(--hg-ink);
      color: var(--hg-ink);
    }
    :host([variant='text']) a,
    :host([variant='text']) button {
      background: transparent;
      border-color: transparent;
      padding-inline: 4px;
    }
    :host([variant='text']) a:hover,
    :host([variant='text']) button:hover:not(:disabled) {
      background: transparent;
      color: var(--hg-forest);
    }
    :host([variant='inverse']) a,
    :host([variant='inverse']) button {
      background: var(--hg-ink);
      border-color: var(--hg-ink);
      color: var(--hg-paper);
    }
    :host([size='sm']) a,
    :host([size='sm']) button {
      min-height: 2.15rem;
      padding: 8px 14px;
      font-size: 12.5px;
    }
    :host([block]) {
      display: flex;
    }
    :host([block]) a,
    :host([block]) button {
      width: 100%;
    }
    button:disabled {
      opacity: 0.46;
      cursor: not-allowed;
    }
  `;

  @property({ reflect: true }) variant: 'filled' | 'tonal' | 'outlined' | 'text' | 'inverse' =
    'filled';
  @property({ reflect: true }) size: 'default' | 'sm' = 'default';
  @property() href?: string;
  @property() target?: string;
  @property() rel?: string;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean, reflect: true }) block = false;
  @property() type: 'button' | 'submit' = 'button';

  render() {
    const inner = html`<slot></slot>`;
    if (this.href && !this.disabled) {
      return html`<a href=${this.href} target=${ifDefined(this.target)} rel=${ifDefined(this.rel)}
        >${inner}</a
      >`;
    }
    return html`<button type=${this.type} ?disabled=${this.disabled}>${inner}</button>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'hg-button': HgButton;
  }
}
